import { ChangeDetectionStrategy, Component, computed, input, OnInit, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FinancialAccount } from '@ld.consumer-portal/models';
import { CurrencyPipe, SlicePipe } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { errorIcon } from '@ld.consumer-portal/data-uri';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';

export interface SelectAccountsEvent {
  selectedAccounts: FinancialAccount[];
}

@Component({
  selector: 'ld-select-accounts-modal',
  imports: [
    BaseModalComponent,
    FormsModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    CurrencyPipe,
    SlicePipe,
    TranslocoDirective,
  ],
  templateUrl: './select-accounts-modal.component.html',
  styleUrl: './select-accounts-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectAccountsModalComponent implements OnInit {
  protected readonly errorIcon = errorIcon;

  readonly accounts = input.required<FinancialAccount[]>();

  readonly bankName = input<string>('Your Bank');

  readonly bankLogo = input<string | null>(null);

  readonly isLoading = input<boolean>(false);

  readonly errorMessage = input<string | null>(null);

  readonly confirm = output<SelectAccountsEvent>();

  readonly back = output<void>();

  readonly cancel = output<void>();

  protected readonly selectedAccountIds = signal<Set<string>>(new Set());

  ngOnInit(): void {
    const accounts = this.accounts();
    if (accounts.length > 0) {
      const allIds = new Set(accounts.map((a) => a.accountId));
      this.selectedAccountIds.set(allIds);
    }
  }

  protected readonly hasSelection = computed(() => this.selectedAccountIds().size > 0);

  protected readonly selectedAccounts = computed(() => {
    const selectedIds = this.selectedAccountIds();
    return this.accounts().filter((a) => selectedIds.has(a.accountId));
  });

  protected readonly totalSelectedBalance = computed(() => {
    return this.selectedAccounts().reduce((sum, a) => sum + (a.balance || 0), 0);
  });

  protected readonly hasAccounts = computed(() => this.accounts().length > 0);

  protected isSelected(account: FinancialAccount): boolean {
    return this.selectedAccountIds().has(account.accountId);
  }

  protected toggleAccount(account: FinancialAccount): void {
    const current = new Set(this.selectedAccountIds());
    if (current.has(account.accountId)) {
      current.delete(account.accountId);
    } else {
      current.add(account.accountId);
    }
    this.selectedAccountIds.set(current);
  }

  protected selectAll(): void {
    const allIds = new Set(this.accounts().map((a) => a.accountId));
    this.selectedAccountIds.set(allIds);
  }

  protected deselectAll(): void {
    this.selectedAccountIds.set(new Set());
  }

  protected onConfirm(): void {
    if (!this.isLoading() && this.hasSelection()) {
      this.confirm.emit({ selectedAccounts: this.selectedAccounts() });
    }
  }

  protected onBack(): void {
    if (!this.isLoading()) {
      this.back.emit();
    }
  }

  protected onCancel(): void {
    if (!this.isLoading()) {
      this.cancel.emit();
    }
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget && !this.isLoading()) {
      this.cancel.emit();
    }
  }
}
